import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Store, MapPin, CheckCircle2, XCircle, ExternalLink } from "lucide-react";

export default function AdminRetailers() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [acting, setActing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/retailers/pending");
      setPending(data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load retailer queue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const decide = async (user, status, remarksText) => {
    setActing(true);
    try {
      await api.post(`/admin/retailers/${user.id}/decision`, {
        status, remarks: remarksText || null,
      });
      toast.success(`Retailer ${status}`);
      setRejectOpen(false); setRemarks(""); setSelected(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Action failed");
    } finally {
      setActing(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="animate-fade-up">
        <span className="overline">Admin</span>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
          Retailer Applications
        </h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Review GST + geolocation from Hitch Zone users applying to become Retailers.
          Approval promotes them to <code>role=partner</code> and unlocks BBPS + payouts.
        </p>

        <div className="mt-8 surface-card overflow-hidden">
          <div className="p-4 flex items-center justify-between">
            <Badge variant="outline" className="font-mono text-[10px]" data-testid="retailer-pending-count">
              {pending.length} pending
            </Badge>
            <Button variant="outline" size="sm" onClick={load} data-testid="retailer-refresh">Refresh</Button>
          </div>
          <Table data-testid="retailer-queue-table">
            <TableHeader>
              <TableRow>
                <TableHead>Applicant</TableHead>
                <TableHead>GST</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : pending.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground">No retailer applications pending.</TableCell></TableRow>
              ) : (
                pending.map((u) => {
                  const a = u.retailer_application || {};
                  const mapsHref = `https://www.google.com/maps?q=${a.latitude},${a.longitude}`;
                  return (
                    <TableRow key={u.id} data-testid={`retailer-row-${u.id}`}>
                      <TableCell>
                        <div className="font-medium">{u.full_name}</div>
                        <div className="text-xs text-muted-foreground">{u.email} · {u.mobile}</div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{a.gst_number || "—"}</TableCell>
                      <TableCell>
                        <a href={mapsHref} target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-center gap-1 text-primary hover:underline text-xs">
                          <MapPin className="h-3.5 w-3.5" />
                          {a.city || a.state || "View on map"}
                          <ExternalLink className="h-3 w-3 opacity-60" />
                        </a>
                        <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                          {a.latitude?.toFixed?.(4)}, {a.longitude?.toFixed?.(4)}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground font-mono">
                        {a.submitted_at ? new Date(a.submitted_at).toLocaleString("en-IN") : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => decide(u, "verified")}
                            disabled={acting}
                            data-testid={`approve-retailer-${u.id}`}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" />Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setSelected(u); setRejectOpen(true); setRemarks(""); }}
                            disabled={acting}
                            data-testid={`reject-retailer-${u.id}`}
                          >
                            <XCircle className="h-4 w-4 mr-1" />Reject
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Reject Retailer application</DialogTitle>
              <DialogDescription>
                The applicant ({selected?.full_name}) will see this reason and can re-apply.
              </DialogDescription>
            </DialogHeader>
            <Textarea
              data-testid="retailer-reject-remarks"
              placeholder="Reason (shown to the applicant)"
              rows={4}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
              <Button
                variant="destructive"
                disabled={acting || !remarks.trim()}
                onClick={() => decide(selected, "rejected", remarks.trim())}
                data-testid="confirm-retailer-reject"
              >
                Reject
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
